import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';

/**
 * DTO retornado pelo backend nos endpoints de consulta (getComplaintById / getComplaintByDate).
 * Espelha o ComplaintResponseDTO do Java.
 */
export interface ComplaintResponseDTO {
  id: number;
  idDenunciaApp?: number;
  address?: string;
  complaintType?: {
    id?: number;
    description: string;
  };
  text?: string;
  registerDate: string;
  complaintStatus?: {
    id?: number;
    description: string;
  };
  involvedList?: any[];
  imageList?: {
    id: number;
    imageUrl: string;
  }[];
  createdAt?: string;
  updatedAt?: string;
  deletedAt?: string;
  latitude?: string;
  longitude?: string;
}

/**
 * Resposta dos endpoints de manual-execution.
 */
export interface ManualExecutionResponse {
  success: boolean;
  message: string;
  executionId: string;
  [key: string]: any;
}

/**
 * Metadados de uma execução ativa.
 */
export interface ExecutionMetadata {
  type: string;
  parameters: Record<string, string>;
  startTime: string;
  endTime?: string | null;
  completed: boolean;
  totalSaved: number;
}

/** Resposta de /executions/active */
export interface ActiveExecutionsResponse {
  success: boolean;
  activeExecutions: number;
  executions: Record<string, ExecutionMetadata>;
}

/** Resposta de /execution/{id}/status */
export interface ExecutionStatusResponse {
  success: boolean;
  executionId: string;
  type: string;
  parameters: Record<string, string>;
  startTime: string;
  endTime?: string | null;
  completed: boolean;
  totalSaved: number;
  currentProgress?: {
    processed: number;
    total: number;
    percentage: number;
    message: string;
  };
  durationSeconds?: number;
  sseUrl: string;
}

/**
 * Evento de progresso emitido via SSE.
 * Exemplo real do backend:
 * {"currentId":565,"idInicial":550,"idFinal":600,"processed":16,
 *  "total":51,"percentComplete":31.37,"savedCount":15,
 *  "status":"PROCESSING","message":"ID 565 salvo com sucesso"}
 */
export interface ProgressEventDTO {
  currentId: number;
  idInicial: number;
  idFinal: number;
  processed: number;
  total: number;
  percentComplete: number;
  savedCount: number;
  status: 'PROCESSING' | 'COMPLETED' | 'FAILED';
  message: string;
}

@Injectable({
  providedIn: 'root'
})
export class ComplaintApiService {

  private readonly baseUrl = `${environment.apiUrl}/nucon/api/v1/complaint-api`;

  private readonly httpOptions = {
    headers: new HttpHeaders({
      'Content-Type': 'application/x-www-form-urlencoded'
    })
  };

  constructor(private http: HttpClient) {}


  /** POST /manual-execution — Dispara busca manual por faixa de IDs */
  executeManualFetch(idInicial: number, idFinal: number): Observable<ManualExecutionResponse> {
    const params = new HttpParams()
      .set('idInicial', idInicial.toString())
      .set('idFinal', idFinal.toString());

    return this.http.post<ManualExecutionResponse>(
      this.baseUrl + '/manual-execution',
      null,
      { ...this.httpOptions, params }
    );
  }

  /** POST /manual-execution-by-date — Dispara busca manual por data única */
  executeManualByDate(data: string): Observable<ManualExecutionResponse> {
    const params = new HttpParams().set('data', data);
    return this.http.post<ManualExecutionResponse>(
      `${this.baseUrl}/manual-execution-by-date`, null, { ...this.httpOptions, params }
    );
  }

  /** POST /manual-execution-by-date-range — Dispara busca manual por intervalo de datas */
  executeManualByDateRange(dataInicial: string, dataFinal: string): Observable<ManualExecutionResponse> {
    const params = new HttpParams()
      .set('dataInicial', dataInicial)
      .set('dataFinal', dataFinal);
    return this.http.post<ManualExecutionResponse>(
      `${this.baseUrl}/manual-execution-by-date-range`, null, { ...this.httpOptions, params }
    );
  }

  /**
   * Conecta-se ao stream SSE de progresso e retorna um Observable que emite
   * cada evento de progresso. O stream é encerrado quando o backend enviar
   * status COMPLETED ou FAILED, ou quando o consumidor cancelar a inscrição.
   *
   * Usa fetch() em vez de EventSource para enviar o header Authorization.
   */
  streamProgress(executionId: string): Observable<ProgressEventDTO> {
    return new Observable<ProgressEventDTO>((observer) => {
      const url = `${this.baseUrl}/progress/${encodeURIComponent(executionId)}`;
      const token = sessionStorage.getItem('auth-token') ?? '';

      const controller = new AbortController();

      fetch(url, {
        method: 'GET',
        headers: {
          'Accept': 'text/event-stream',
          'Authorization': `Bearer ${token}`,
        },
        signal: controller.signal,
      })
        .then(async (response) => {
          if (!response.ok || !response.body) {
            observer.error(new Error(`SSE connection failed: ${response.status}`));
            return;
          }

          const reader = response.body.getReader();
          const decoder = new TextDecoder();
          let buffer = '';

          try {
            while (true) {
              const { done, value } = await reader.read();
              if (done) break;

              buffer += decoder.decode(value, { stream: true });
              const lines = buffer.split('\n');
              buffer = lines.pop() ?? '';

              for (const line of lines) {
                if (line.startsWith('data:')) {
                  const json = line.substring(5).trim();
                  if (!json) continue;
                  try {
                    const data: ProgressEventDTO = JSON.parse(json);
                    observer.next(data);
                    if (data.status === 'COMPLETED' || data.status === 'FAILED') {
                      reader.cancel();
                      observer.complete();
                      return;
                    }
                  } catch (_) { /* ignora linhas não-JSON */ }
                }
              }
            }
            observer.complete();
          } catch (err: any) {
            if (err?.name !== 'AbortError') {
              observer.error(err);
            }
          }
        })
        .catch((err) => {
          if (err?.name !== 'AbortError') {
            observer.error(err);
          }
        });

      // Cleanup: aborta o fetch quando o consumidor cancelar a inscrição
      return () => {
        controller.abort();
      };
    });
  }

  /** GET /executions/active — Lista execuções ativas no backend */
  getActiveExecutions(): Observable<ActiveExecutionsResponse> {
    return this.http.get<ActiveExecutionsResponse>(
      `${this.baseUrl}/executions/active`, this.httpOptions
    );
  }

  /** GET /execution/{executionId}/status — Status detalhado de uma execução */
  getExecutionStatus(executionId: string): Observable<ExecutionStatusResponse> {
    return this.http.get<ExecutionStatusResponse>(
      `${this.baseUrl}/execution/${encodeURIComponent(executionId)}/status`, this.httpOptions
    );
  }

  /** GET /progress/stats — Estatísticas gerais de progresso */
  getProgressStats(): Observable<any> {
    return this.http.get<any>(`${this.baseUrl}/progress/stats`, this.httpOptions);
  }

    /** GET /{id} — Consulta denúncia por ID */
    getComplaintById(id: string): Observable<ComplaintResponseDTO> {
      return this.http.get<ComplaintResponseDTO>(
        `${this.baseUrl}/${encodeURIComponent(id)}`,
        this.httpOptions
      );
    }
  
    /** GET /date/{date} — Consulta denúncia por data (formato esperado: yyyy-MM-dd) */
    getComplaintByDate(date: string): Observable<ComplaintResponseDTO> {
      return this.http.get<ComplaintResponseDTO>(
        `${this.baseUrl}/date/${encodeURIComponent(date)}`,
        this.httpOptions
      );
    }


}
